/**
 * IMAGE UTILITIES FOR FRONTEND
 * Uses REACT_APP_API_URL so images work in both local and production.
 */

import { API_BASE_URL } from '../config';

/**
 * Build image URL based on environment with proper URL encoding
 * @param {string} imagePath - The image path from the API
 * @returns {string} - Properly formatted and encoded image URL
 */
export const buildImageUrl = (imagePath) => {
  if (!imagePath) {
    return getPlaceholderImage();
  }

  if (imagePath.startsWith('data:')) {
    return imagePath;
  }

  let finalUrl = imagePath;

  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    finalUrl = imagePath;
  } else if (imagePath.startsWith('/')) {
    finalUrl = `${API_BASE_URL}${imagePath}`;
  } else {
    finalUrl = `${API_BASE_URL}/images/handbags/${imagePath}`;
  }

  // Encode filename for spaces/special chars
  try {
    const pathOnly = finalUrl.split('?')[0];
    const pathParts = pathOnly.startsWith('http')
      ? new URL(pathOnly).pathname.split('/').filter(Boolean)
      : pathOnly.split('/').filter(Boolean);
    const filename = pathParts[pathParts.length - 1];
    if (filename && (filename.includes(' ') || filename.includes('%'))) {
      pathParts[pathParts.length - 1] = encodeURIComponent(decodeURIComponent(filename));
      const prefix = pathOnly.startsWith('http') ? new URL(pathOnly).origin + '/' : '/';
      return prefix + pathParts.join('/');
    }
  } catch (e) {
    console.warn('Failed to parse URL for encoding:', finalUrl, e);
  }

  return finalUrl;
};

/**
 * Get placeholder image URL
 * @returns {string} - Placeholder image data URL
 */
export const getPlaceholderImage = () => {
  return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y3ZjdmNyIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+Cjwvc3ZnPgo=';
};

/**
 * Fix image URLs in data from API
 * @param {Array|Object} data - Data containing image URLs
 * @returns {Array|Object} - Data with fixed image URLs
 */
export const fixImageUrls = (data) => {
  if (Array.isArray(data)) {
    return data.map(item => fixImageUrls(item));
  }

  if (data && typeof data === 'object') {
    const fixed = { ...data };
    
    // Fix main image URL
    if (fixed.image) {
      fixed.image = buildImageUrl(fixed.image);
    }
    
    // Fix variant images if they exist
    if (fixed.variantImages && Array.isArray(fixed.variantImages)) {
      fixed.variantImages = fixed.variantImages.map(img => buildImageUrl(img));
    }
    
    return fixed;
  }

  return data;
};

const imageUtils = {
  buildImageUrl,
  getPlaceholderImage,
  fixImageUrls
};

export default imageUtils;
